/**
 * Proposal Section Renderers
 *
 * These components render structured proposal section data with beautiful UI.
 * No markdown parsing - each field is rendered with dedicated styling.
 */

import React from 'react';
import {
  CoverLetterContent,
  ExecutiveSummaryContent,
  TechnicalApproachContent,
  ManagementApproachContent,
  PastPerformanceContent,
  PersonnelContent,
  PricingContent,
  ComplianceContent,
} from '../../types/proposal-sections';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  Calendar,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  User,
  Briefcase,
  Shield,
  DollarSign,
  Users,
  Target,
  Award,
  Calculator,
  ClipboardCheck,
} from 'lucide-react';
import { EditableText } from './ProposalEditContext';
import { OrgChartView } from './OrgChartView';
import { decodeHtmlEntities, formatClosing, isPlaceholder, stripBracketAnnotations } from '../../lib/export/textCleanup';
import { useJobDescriptionLauncher } from '../personnel/JobDescriptionContext';

// "Generate Job Description" action — only renders inside the on-screen proposal
// previewer (a JobDescriptionProvider supplies the launcher); absent in exports.
function GenerateJDButton({ role }: { role: any }) {
  const launcher = useJobDescriptionLauncher();
  if (!launcher?.available) return null;
  const saved = launcher.hasSaved(role);
  return (
    <button
      type="button"
      onClick={() => launcher.open(role)}
      className={`no-print mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
        saved
          ? 'border border-bw-primary text-bw-primary hover:bg-bw-primary-tint'
          : 'bg-bw-primary text-white hover:bg-blue-700'
      }`}
    >
      <Briefcase className="h-3.5 w-3.5" />
      {saved ? 'View Job Description' : 'Generate Job Description'}
    </button>
  );
}

// ============================================================================
// UTILITY: Citation Highlighter
// ============================================================================

// Coerce any AI-returned value to a safe display string. Regenerated sections
// occasionally return arrays/objects where a string is expected; never let that
// crash the renderer (e.g. "x.split is not a function"). HTML entities are
// decoded so the UI/export never shows raw `&apos;`/`&amp;`.
export function toDisplayString(v: any): string {
  if (v == null) return '';
  if (typeof v === 'string') return decodeHtmlEntities(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toDisplayString).filter(Boolean).join('\n');
  try { return JSON.stringify(v); } catch { return String(v); }
}

// Safely render a list-ish AI value as a separated string. The payload sometimes
// returns a plain string (or object) where an array is expected; calling .join on
// that crashes the renderer (e.g. "components.join is not a function"). Arrays are
// joined; a non-empty string is returned as-is; anything else yields ''.
export function joinList(v: any, sep: string = ', '): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(toDisplayString).filter(Boolean).join(sep);
  if (typeof v === 'string') return decodeHtmlEntities(v).trim();
  return toDisplayString(v);
}

function highlightCitations(text: any): React.ReactNode {
  const s = toDisplayString(text);
  if (!s) return null;

  // Split on citation patterns like [PP-001] or [PERSONNEL-002, inferred...]
  const parts = s.split(/(\[[A-Z]+-\d+(?:,\s*[^\]]+)?\])/g);

  return parts.map((part, index) => {
    if (part.match(/^\[[A-Z]+-\d+/)) {
      return (
        <span
          key={index}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-bw-primary-tint text-bw-primary dark:bg-blue-900/30 dark:text-blue-300"
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

// ============================================================================
// COVER LETTER RENDERER
// ============================================================================

interface CoverLetterRendererProps {
  content: CoverLetterContent;
  showCitations?: boolean;
}

export function CoverLetterRenderer({ content, showCitations = true }: CoverLetterRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      {/* Company Header */}
      <div className="mb-8 pb-6 border-b border-bw-border dark:border-gray-700">
        <h2 className="text-2xl font-bold text-bw-ink dark:text-white mb-2">
          {content.companyInfo.name}
        </h2>
        <div className="text-bw-muted dark:text-bw-muted space-y-1">
          <p>{content.companyInfo.address}</p>
          <p>{content.companyInfo.cityStateZip}</p>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="flex items-center gap-1">
              <Phone className="w-4 h-4" />
              {content.companyInfo.phone}
            </span>
            <span className="flex items-center gap-1">
              <Mail className="w-4 h-4" />
              {content.companyInfo.email}
            </span>
            {content.companyInfo.website && (
              <span className="flex items-center gap-1">
                <Globe className="w-4 h-4" />
                {content.companyInfo.website}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Date */}
      <p className="text-bw-ink dark:text-gray-300 mb-6">{content.date}</p>

      {/* Via Electronic Submission */}
      <p className="font-semibold text-bw-ink dark:text-white mb-4">VIA ELECTRONIC SUBMISSION</p>

      {/* Recipient */}
      <div className="mb-6">
        {content.recipient.name && (
          <p className="text-bw-ink dark:text-gray-300 font-medium">{content.recipient.name}</p>
        )}
        {content.recipient.title && (
          <p className="text-bw-ink dark:text-gray-300">{content.recipient.title}</p>
        )}
        {content.recipient.office && (
          <p className="text-bw-ink dark:text-gray-300">{content.recipient.office}</p>
        )}
        <p className="text-bw-ink dark:text-gray-300">{content.recipient.agency}</p>
        {content.recipient.address &&
          content.recipient.address.split('\n').filter(Boolean).map((line, i) => (
            <p key={i} className="text-bw-ink dark:text-gray-300">{line}</p>
          ))}
      </div>

      {/* Subject */}
      <div className="mb-6 p-4 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
        <p className="font-semibold text-bw-ink dark:text-white">
          RE: {content.subject.contractTitle}
        </p>
        {content.subject.solicitationNumber && (
          <p className="text-bw-ink dark:text-gray-300">
            <strong>Solicitation Number:</strong> {content.subject.solicitationNumber}
          </p>
        )}
        {content.subject.naicsCode && (
          <p className="text-bw-ink dark:text-gray-300">
            <strong>NAICS Code:</strong> {content.subject.naicsCode}
          </p>
        )}
      </div>

      {/* Salutation */}
      <p className="text-bw-ink dark:text-gray-300 mb-6">
        {content.recipient.salutation
          || `Dear ${content.recipient.name || content.recipient.title || 'Contracting Officer'}:`}
      </p>

      {/* Body Paragraphs */}
      <div className="space-y-4 mb-8">
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="body.opening" value={content.body.opening} render={renderText} />
        </p>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="body.qualifications" value={content.body.qualifications} render={renderText} />
        </p>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="body.valueProposition" value={content.body.valueProposition} render={renderText} />
        </p>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="body.understanding" value={content.body.understanding} render={renderText} />
        </p>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="body.closing" value={content.body.closing} render={renderText} />
        </p>
      </div>

      {/* Signature — closing without doubled punctuation; hide unfilled placeholders */}
      <div className="mb-8">
        <p className="text-bw-ink dark:text-gray-300 mb-4">
          {formatClosing(content.signature.salutation) || 'Respectfully submitted,'}
        </p>
        <div className="mt-8">
          {!isPlaceholder(content.signature.name) && (
            <p className="font-semibold text-bw-ink dark:text-white">{content.signature.name}</p>
          )}
          {!isPlaceholder(content.signature.title) && (
            <p className="text-bw-muted dark:text-bw-muted">{content.signature.title}</p>
          )}
          {!isPlaceholder(content.signature.company) && (
            <p className="text-bw-muted dark:text-bw-muted">{content.signature.company}</p>
          )}
        </div>
      </div>

      {/* Enclosures */}
      {content.enclosures && content.enclosures.length > 0 && (
        <div className="pt-6 border-t border-bw-border dark:border-gray-700">
          <p className="font-semibold text-bw-ink dark:text-white mb-2">Enclosures:</p>
          <ul className="list-disc list-inside text-bw-ink dark:text-gray-300">
            {content.enclosures.map((enc, idx) => (
              <li key={idx}>{enc}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EXECUTIVE SUMMARY RENDERER
// ============================================================================

interface ExecutiveSummaryRendererProps {
  content: ExecutiveSummaryContent;
  showCitations?: boolean;
}

export function ExecutiveSummaryRenderer({ content, showCitations = true }: ExecutiveSummaryRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="space-y-8">
      {/* Value Proposition */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-bw-primary" />
          Value Proposition
        </h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="valueProposition" value={content.valueProposition} render={renderText} />
        </p>
      </section>

      {/* Understanding of Requirements */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-bw-primary" />
          Understanding of Requirements
        </h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="understandingOfRequirements" value={content.understandingOfRequirements} render={renderText} />
        </p>
      </section>

      {/* Key Discriminators */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-bw-primary" />
          Key Discriminators
        </h2>
        <div className="space-y-4">
          {content.keyDiscriminators.map((disc, idx) => (
            <div
              key={idx}
              className="p-4 bg-bw-surface-subtle dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border-l-4 border-blue-500"
            >
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">
                {idx + 1}. <EditableText path={`keyDiscriminators.${idx}.title`} value={disc.title} multiline={false} />
              </h3>
              <p className="text-bw-ink dark:text-gray-300">
                <EditableText path={`keyDiscriminators.${idx}.description`} value={disc.description} render={renderText} />
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Relevant Experience Table */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-bw-primary" />
          Relevant Experience
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
            <thead className="bg-bw-surface-subtle dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase tracking-wider">Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase tracking-wider">Relevance</th>
              </tr>
            </thead>
            <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
              {content.relevantExperience.map((exp, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium">{exp.project}</td>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{exp.client}</td>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{exp.value}</td>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{exp.relevance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Technical Overview */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Technical Solution Overview</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="technicalOverview" value={content.technicalOverview} render={renderText} />
        </p>
      </section>

      {/* Management Overview */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Management Approach Summary</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="managementOverview" value={content.managementOverview} render={renderText} />
        </p>
      </section>

      {/* Conclusion */}
      <section className="p-6 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Conclusion</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="conclusion" value={content.conclusion} render={renderText} />
        </p>
      </section>
    </div>
  );
}

// ============================================================================
// TECHNICAL APPROACH RENDERER
// ============================================================================

interface TechnicalApproachRendererProps {
  content: TechnicalApproachContent;
  showCitations?: boolean;
}

export function TechnicalApproachRenderer({ content, showCitations = true }: TechnicalApproachRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="space-y-8">
      {/* Scope Overview */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-bw-primary" />
          Scope Overview
        </h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="scopeOverview" value={content.scopeOverview} render={renderText} />
        </p>
      </section>

      {/* Technical Challenges */}
      {content.technicalChallenges && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Technical Challenges & Approach
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            <EditableText path="technicalChallenges" value={content.technicalChallenges} render={renderText} />
          </p>
        </section>
      )}

      {/* Methodology */}
      {content.methodology && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-bw-primary" />
            Technical Methodology
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed mb-4">
            <EditableText path="methodology.overallApproach" value={content.methodology.overallApproach} render={renderText} />
          </p>

          {content.methodology.tasks && content.methodology.tasks.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-bw-ink dark:text-white">Task Breakdown</h3>
              {content.methodology.tasks.map((task, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-bw-surface-subtle dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border-l-4 border-blue-500"
                >
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2">
                    Task {idx + 1}: <EditableText path={`methodology.tasks.${idx}.name`} value={task.name} multiline={false} />
                  </h4>
                  <p className="text-bw-ink dark:text-gray-300 mb-3">
                    <EditableText path={`methodology.tasks.${idx}.approach`} value={task.approach} render={renderText} />
                  </p>
                  {joinList(task.tools) && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Tools: </span>
                      <span className="text-sm text-bw-ink dark:text-gray-300">{joinList(task.tools)}</span>
                    </div>
                  )}
                  {joinList(task.deliverables) && (
                    <div>
                      <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Deliverables: </span>
                      <span className="text-sm text-bw-ink dark:text-gray-300">{joinList(task.deliverables)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Tools & Technologies */}
      {content.toolsAndTechnologies && content.toolsAndTechnologies.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Tools & Technologies
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Technology</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Purpose</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.toolsAndTechnologies.map((tool, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium">{tool.category}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{tool.technology}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{tool.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Innovation */}
      {content.innovation && (
        <section className="p-6 bg-bw-primary-tint dark:bg-purple-900/20 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-bw-primary" />
            Innovation & Value-Added Features
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            <EditableText path="innovation" value={content.innovation} render={renderText} />
          </p>
        </section>
      )}

      {/* Quality Assurance */}
      {content.qualityAssurance && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Quality Assurance
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            <EditableText path="qualityAssurance" value={content.qualityAssurance} render={renderText} />
          </p>
        </section>
      )}

      {/* Schedule */}
      {content.schedule && content.schedule.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-bw-primary" />
            Implementation Schedule
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Phase</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Milestones</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Deliverables</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.schedule.map((phase, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium">{phase.phase}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{phase.duration}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{phase.milestones}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{phase.deliverables}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// MANAGEMENT APPROACH RENDERER
// ============================================================================

interface ManagementApproachRendererProps {
  content: ManagementApproachContent;
  showCitations?: boolean;
}

export function ManagementApproachRenderer({ content, showCitations = true }: ManagementApproachRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="space-y-8">
      {/* Corporate Organization */}
      {content.corporateOrganization && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-bw-primary" />
            Corporate Organization
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            <EditableText path="corporateOrganization" value={content.corporateOrganization} render={renderText} />
          </p>
        </section>
      )}

      {/* Organization Chart */}
      {content.organizationChart && (
        <section className="p-6 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Organization Structure
          </h2>
          <OrgChartView chart={toDisplayString(content.organizationChart)} />
        </section>
      )}

      {/* Key Roles */}
      {content.keyRoles && content.keyRoles.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-bw-primary" />
            Key Management Roles
          </h2>
          <div className="space-y-4">
            {content.keyRoles.map((role, idx) => (
              <div
                key={idx}
                className="p-4 bg-bw-surface dark:bg-gray-800 rounded-lg shadow-sm border border-bw-border dark:border-gray-700"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-bw-primary-tint dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-bw-primary dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-bw-ink dark:text-white">
                      {role.role}
                      {role.name && <span className="text-bw-muted dark:text-bw-muted font-normal"> - {role.name}</span>}
                      {role.evidence && showCitations && (
                        <span className="ml-2 text-sm font-normal text-bw-primary dark:text-blue-400">
                          {role.evidence}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-bw-muted dark:text-bw-muted mb-2">
                      <EditableText path={`keyRoles.${idx}.qualifications`} value={role.qualifications} render={renderText} />
                    </p>
                    {role.responsibilities && role.responsibilities.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium text-bw-ink dark:text-gray-300">Responsibilities:</span>
                        <ul className="list-disc list-inside text-sm text-bw-ink dark:text-gray-300 mt-1">
                          {role.responsibilities.map((resp, ridx) => (
                            <li key={ridx}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <GenerateJDButton role={role} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Communication Plan */}
      {content.communicationPlan && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Communication Plan
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed mb-4">
            <EditableText path="communicationPlan.reportingStructure" value={content.communicationPlan.reportingStructure} render={renderText} />
          </p>
          {content.communicationPlan.meetings && content.communicationPlan.meetings.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
                <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Meeting Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Frequency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Attendees</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Purpose</th>
                  </tr>
                </thead>
                <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                  {content.communicationPlan.meetings.map((meeting, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium">{meeting.type}</td>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{meeting.frequency}</td>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{meeting.attendees}</td>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{meeting.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Quality Management */}
      {content.qualityManagement && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-success" />
            Quality Management
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed mb-4">
            <EditableText path="qualityManagement.process" value={content.qualityManagement.process} render={renderText} />
          </p>
          {content.qualityManagement.metrics && content.qualityManagement.metrics.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
                <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Metric</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Measurement Method</th>
                  </tr>
                </thead>
                <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                  {content.qualityManagement.metrics.map((metric, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium">{metric.metric}</td>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{metric.target}</td>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{metric.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Risk Management */}
      {content.riskManagement && content.riskManagement.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-fit-moderate" />
            Risk Management
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Risk</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Likelihood</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Impact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Mitigation</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.riskManagement.map((risk, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white">{risk.risk}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        risk.likelihood === 'High' ? 'bg-urgent-bg text-urgent' :
                        risk.likelihood === 'Medium' ? 'bg-fit-moderate-bg text-fit-moderate' :
                        'bg-success-bg text-success'
                      }`}>
                        {risk.likelihood}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        risk.impact === 'High' ? 'bg-urgent-bg text-urgent' :
                        risk.impact === 'Medium' ? 'bg-fit-moderate-bg text-fit-moderate' :
                        'bg-success-bg text-success'
                      }`}>
                        {risk.impact}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{risk.mitigation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Transition Plan */}
      {content.transitionPlan && (
        <section className="p-6 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">
            Transition Plan
          </h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            <EditableText path="transitionPlan" value={content.transitionPlan} render={renderText} />
          </p>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// PAST PERFORMANCE RENDERER
// ============================================================================

interface PastPerformanceRendererProps {
  content: PastPerformanceContent;
  showCitations?: boolean;
}

export function PastPerformanceRenderer({ content, showCitations = true }: PastPerformanceRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="space-y-8">
      {/* Summary */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Summary of Relevant Experience</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          <EditableText path="summary" value={content.summary} render={renderText} />
        </p>
      </section>

      {/* Contracts */}
      <section>
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-6">Past Performance Narratives</h2>
        <div className="space-y-6">
          {content.contracts.map((contract, idx) => (
            <div
              key={idx}
              className="p-6 bg-bw-surface dark:bg-gray-800 rounded-xl shadow-sm border border-bw-border dark:border-gray-700"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-bw-ink dark:text-white">
                    {contract.name}
                    {contract.evidence && showCitations && (
                      <span className="ml-2 text-sm font-normal text-bw-primary dark:text-blue-400">
                        {contract.evidence}
                      </span>
                    )}
                  </h3>
                  <p className="text-bw-muted dark:text-bw-muted">{contract.client}</p>
                </div>
                <span className="px-3 py-1 bg-success-bg dark:bg-green-900/30 text-success dark:text-green-300 rounded-full text-sm font-medium">
                  {contract.value}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                {contract.contractNumber && (
                  <div>
                    <span className="font-medium text-bw-muted dark:text-bw-muted">Contract #:</span>
                    <span className="ml-2 text-bw-ink dark:text-white">{contract.contractNumber}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-bw-muted dark:text-bw-muted">Period:</span>
                  <span className="ml-2 text-bw-ink dark:text-white">{contract.period}</span>
                </div>
                {contract.contractType && (
                  <div>
                    <span className="font-medium text-bw-muted dark:text-bw-muted">Type:</span>
                    <span className="ml-2 text-bw-ink dark:text-white">{contract.contractType}</span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-bw-ink dark:text-white mb-2">Scope of Work</h4>
                <p className="text-bw-ink dark:text-gray-300 text-sm">{renderText(contract.scopeOfWork)}</p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-bw-ink dark:text-white mb-2">Relevance to Current Requirement</h4>
                <p className="text-bw-ink dark:text-gray-300 text-sm">{renderText(contract.relevance)}</p>
              </div>

              {contract.achievements && contract.achievements.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2">Key Achievements</h4>
                  <ul className="list-disc list-inside text-bw-ink dark:text-gray-300 text-sm space-y-1">
                    {contract.achievements.map((achievement, aidx) => (
                      <li key={aidx}>{renderText(achievement)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {contract.reference && (
                <div className="pt-4 border-t border-bw-border dark:border-gray-700">
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2">Reference</h4>
                  <div className="text-sm text-bw-ink dark:text-gray-300">
                    <p className="font-medium">{contract.reference.name}</p>
                    <p>{contract.reference.title}</p>
                    {contract.reference.phone && <p>{contract.reference.phone}</p>}
                    {contract.reference.email && <p>{contract.reference.email}</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Relevance Summary */}
      <section className="p-6 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg">
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Relevance Summary</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
          {renderText(content.relevanceSummary)}
        </p>
      </section>
    </div>
  );
}

// ============================================================================
// PERSONNEL RENDERER
// ============================================================================

interface PersonnelRendererProps {
  content: PersonnelContent;
  showCitations?: boolean;
}

export function PersonnelRenderer({ content, showCitations = true }: PersonnelRendererProps) {
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  return (
    <div className="space-y-8">
      {/* Summary */}
      {content.summary && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Personnel Summary</h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">
            {renderText(content.summary)}
          </p>
        </section>
      )}

      {/* Key Personnel Cards */}
      {content.keyPersonnel && content.keyPersonnel.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-6">Key Personnel</h2>
          <div className="space-y-6">
            {content.keyPersonnel.map((person, idx) => (
            <div
              key={idx}
              className="p-6 bg-bw-surface dark:bg-gray-800 rounded-xl shadow-sm border border-bw-border dark:border-gray-700"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-bw-primary-tint dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-bw-primary dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-bw-ink dark:text-white">
                    {person.name}
                    {person.evidence && showCitations && (
                      <span className="ml-2 text-sm font-normal text-bw-primary dark:text-blue-400">
                        {person.evidence}
                      </span>
                    )}
                  </h3>
                  <p className="text-bw-primary dark:text-blue-400 font-medium">{person.role}</p>
                  <p className="text-bw-muted dark:text-bw-muted text-sm">
                    {person.yearsExperience}+ years experience
                    {person.clearance && ` • ${person.clearance} Clearance`}
                  </p>
                </div>
              </div>

              {/* Certifications */}
              {person.certifications && person.certifications.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2 text-sm">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {person.certifications.map((cert, cidx) => (
                      <span
                        key={cidx}
                        className="px-2 py-1 bg-success-bg dark:bg-green-900/30 text-success dark:text-green-300 rounded text-xs"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {person.education && person.education.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2 text-sm">Education</h4>
                  <ul className="text-bw-ink dark:text-gray-300 text-sm space-y-1">
                    {person.education.map((edu, eidx) => (
                      <li key={eidx}>{edu}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Relevant Experience */}
              {person.relevantExperience && person.relevantExperience.length > 0 && (
                <div>
                  <h4 className="font-semibold text-bw-ink dark:text-white mb-2 text-sm">Relevant Experience</h4>
                  <div className="space-y-3">
                    {person.relevantExperience.map((exp, eidx) => (
                      <div key={eidx} className="pl-4 border-l-2 border-bw-border dark:border-gray-700">
                        <p className="font-medium text-bw-ink dark:text-white text-sm">{exp.position}</p>
                        <p className="text-bw-muted dark:text-bw-muted text-xs">{exp.organization} • {exp.years}</p>
                        {exp.responsibilities && exp.responsibilities.length > 0 && (
                          <ul className="text-bw-ink dark:text-gray-300 text-xs mt-1 space-y-0.5">
                            {exp.responsibilities.map((resp, ridx) => (
                              <li key={ridx}>• {resp}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <GenerateJDButton role={person} />
            </div>
          ))}
          </div>
        </section>
      )}

      {/* Staffing Plan Table */}
      {content.staffingPlan && content.staffingPlan.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Staffing Plan</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Labor Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">FTE</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Location</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.staffingPlan.map((staff, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white">{staff.category}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{staff.quantity}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{staff.fte}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{staff.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Recruitment & Substitution */}
      {(content.recruitmentApproach || content.substitutionProcess) && (
        <section className="grid md:grid-cols-2 gap-6">
          {content.recruitmentApproach && (
            <div className="p-4 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Recruitment & Retention</h3>
              <p className="text-bw-ink dark:text-gray-300 text-sm">{content.recruitmentApproach}</p>
            </div>
          )}
          {content.substitutionProcess && (
            <div className="p-4 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Personnel Substitution</h3>
              <p className="text-bw-ink dark:text-gray-300 text-sm">{content.substitutionProcess}</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ============================================================================
// PRICING RENDERER WITH USER-FRIENDLY / FEDERAL TOGGLE
// ============================================================================

interface PricingRendererProps {
  content: PricingContent;
  showCitations?: boolean;
  defaultView?: 'friendly' | 'federal';
  /** Hide the view-mode toggle entirely (used by export/print). */
  hideToggle?: boolean;
}

export function PricingRenderer({ content, showCitations = true, defaultView = 'friendly', hideToggle = false }: PricingRendererProps) {
  const [viewMode, setViewMode] = React.useState<'friendly' | 'federal'>(defaultView ?? 'friendly');
  // Keep the rendered view in sync if the forced default changes (e.g. export).
  React.useEffect(() => { if (defaultView) setViewMode(defaultView); }, [defaultView]);
  const renderText = (text: any) => {
    const s = toDisplayString(text);
    return showCitations ? highlightCitations(s) : stripBracketAnnotations(s);
  };

  // Cast content to any to handle new dynamic fields
  const pricingContent = content as any;

  // Check if we have federal-compliant data (expanded to include new mandatory fields)
  const hasFederalData = !!(
    pricingContent.clinStructure ||
    pricingContent.laborHoursSummary ||
    pricingContent.scaWageDetermination ||
    pricingContent.farTable15_2CoverPage ||
    pricingContent.laborRateBuildup ||
    pricingContent.costCertification ||
    pricingContent.basisOfEstimate
  );

  return (
    <div className="space-y-8">
      {/* View Toggle - hidden in export/print (hideToggle); marked no-print as a
          second guard so it never appears in the PDF. */}
      {!hideToggle && (
      <div className="flex items-center justify-between bg-bw-surface-subtle dark:bg-gray-800 rounded-lg p-2 no-print">
        <span className="text-sm font-medium text-bw-ink dark:text-gray-300 ml-2">View Mode:</span>
        <div className="flex rounded-lg bg-gray-200 dark:bg-gray-700 p-1">
          <button
            onClick={() => setViewMode('friendly')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'friendly'
                ? 'bg-bw-surface dark:bg-gray-600 text-bw-primary dark:text-blue-400 shadow-sm'
                : 'text-bw-muted dark:text-bw-muted hover:text-bw-ink dark:hover:text-gray-200'
            }`}
          >
            User-Friendly
          </button>
          <button
            onClick={() => setViewMode('federal')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'federal'
                ? 'bg-bw-surface dark:bg-gray-600 text-bw-primary dark:text-blue-400 shadow-sm'
                : 'text-bw-muted dark:text-bw-muted hover:text-bw-ink dark:hover:text-gray-200'
            }`}
          >
            Federal Compliance
          </button>
        </div>
      </div>
      )}

      <PricingViewBoundary raw={pricingContent}>
        {viewMode === 'federal' ? (
          <FederalCompliancePricingView content={pricingContent} showCitations={showCitations} renderText={renderText} />
        ) : (
          <UserFriendlyPricingView content={pricingContent} showCitations={showCitations} renderText={renderText} />
        )}
      </PricingViewBoundary>
    </div>
  );
}

// Safety net: a bad/object field in the pricing payload must never white-screen the
// whole workspace. If a pricing view throws while rendering, show a contained
// fallback (with the raw JSON available) instead of crashing the page.
class PricingViewBoundary extends React.Component<
  { raw?: any; children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { raw?: any; children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error('Pricing view render error (contained):', error);
  }
  render() {
    if (this.state.error) {
      let rawText = '';
      try { rawText = JSON.stringify(this.props.raw, null, 2); } catch { rawText = String(this.props.raw); }
      return (
        <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-fit-moderate-bg dark:bg-amber-900/20 p-4">
          <h3 className="text-sm font-bold text-fit-moderate dark:text-amber-300 mb-1">This pricing view couldn't render</h3>
          <p className="text-sm text-fit-moderate dark:text-amber-200 mb-3">
            A field in the pricing data wasn't in the expected format. The underlying pricing is intact — try
            "Recompute Pricing", or switch the view toggle. Raw pricing data is shown below.
          </p>
          <pre className="text-xs whitespace-pre-wrap break-words max-h-96 overflow-auto bg-bw-surface dark:bg-gray-900 p-3 rounded border border-bw-border dark:border-gray-700 text-bw-ink dark:text-gray-300">{rawText}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// FEDERAL COMPLIANCE PRICING VIEW
// ============================================================================

function FederalCompliancePricingView({
  content,
  showCitations,
  renderText
}: {
  content: any;
  showCitations: boolean;
  renderText: (text: string) => React.ReactNode;
}) {
  // Commercial (FAR 12/13) buys must NOT show the FAR Table 15-2 cost apparatus.
  const isCommercialPricing = content.pricingMode?.commercial === true ||
    content.farTable15_2CoverPage?.applicable === false ||
    content.clinPriceSchedule?.applicable === true;

  return (
    <div className="space-y-8">
      {/* Price reasonableness vs. solicitation order floor/ceiling */}
      {content.priceValidation?.warnings?.length > 0 && (
        <section className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-fit-moderate-bg dark:bg-amber-900/20 p-4">
          <h3 className="text-sm font-bold text-fit-moderate dark:text-amber-300 mb-2">Price Check vs. Solicitation</h3>
          <ul className="list-disc pl-5 space-y-1">
            {content.priceValidation.warnings.map((w: string, i: number) => (
              <li key={i} className="text-sm text-fit-moderate dark:text-amber-200">{w}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Commercial buy banner — explains why no FAR 15-2 cost buildup is shown */}
      {isCommercialPricing && (
        <section className="rounded-lg border border-blue-200 dark:border-blue-800 bg-bw-primary-tint dark:bg-blue-900/20 p-4">
          <h3 className="text-sm font-bold text-bw-primary dark:text-blue-300 mb-1">
            Commercial Acquisition (FAR Part {content.pricingMode?.farPart || '12/13'})
          </h3>
          <p className="text-sm text-bw-primary dark:text-blue-200">
            {content.pricingMode?.note || 'Pricing is presented as firm unit and total prices per CLIN. No FAR Table 15-2 cost buildup or certified cost-or-pricing data is required.'}
            {content.pricingMode?.instrument ? ` Submit on: ${content.pricingMode.instrument}.` : ''}
          </p>
        </section>
      )}

      {/* Commercial CLIN price schedule (the artifact the solicitation asks for) */}
      {isCommercialPricing && content.clinPriceSchedule?.clins?.length > 0 && (
        <section className="border-2 border-bw-border dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="bg-gray-800 dark:bg-gray-900 text-white p-3 text-center">
            <h2 className="text-lg font-bold">CLIN PRICE SCHEDULE</h2>
            {content.clinPriceSchedule.source && (
              <p className="text-xs text-gray-300">{content.clinPriceSchedule.source}</p>
            )}
          </div>
          <div className="p-4 bg-bw-surface dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-bw-border dark:border-gray-600 text-left">
                  <th className="py-2 pr-3">CLIN</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3">Unit</th>
                  <th className="py-2 pr-3">Qty</th>
                  <th className="py-2 pr-3">Unit Price</th>
                  <th className="py-2">Total Price</th>
                </tr>
              </thead>
              <tbody>
                {content.clinPriceSchedule.clins.map((c: any, i: number) => (
                  <tr key={i} className="border-b border-bw-border dark:border-gray-700">
                    <td className="py-2 pr-3 font-mono">{toDisplayString(c.clin) || '—'}</td>
                    <td className="py-2 pr-3">{toDisplayString(c.description) || '—'}</td>
                    <td className="py-2 pr-3">{toDisplayString(c.unit) || '—'}</td>
                    <td className="py-2 pr-3">{toDisplayString(c.quantityNote) || '—'}</td>
                    <td className="py-2 pr-3">{toDisplayString(c.unitPrice) || '—'}</td>
                    <td className="py-2">{toDisplayString(c.totalPrice) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {content.clinPriceSchedule.instructions && (
              <p className="text-xs text-bw-muted dark:text-bw-muted mt-3 italic">{content.clinPriceSchedule.instructions}</p>
            )}
          </div>
        </section>
      )}

      {/* FAR Table 15-2 Cover Page - ONLY for FAR 15 cost-type procurements */}
      {content.farTable15_2CoverPage && content.farTable15_2CoverPage.applicable !== false && !isCommercialPricing && (
        <section className="border-2 border-bw-border dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="bg-gray-800 dark:bg-gray-900 text-white p-3 text-center">
            <h2 className="text-lg font-bold">COST/PRICE PROPOSAL COVER SHEET</h2>
            <p className="text-xs text-gray-300">FAR Table 15-2, Section I.A</p>
          </div>
          <div className="p-6 bg-bw-surface dark:bg-gray-800 space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Solicitation Number</label>
                  <p className="text-lg font-mono text-bw-ink dark:text-white border-b border-bw-border dark:border-gray-600 pb-1">
                    {content.farTable15_2CoverPage.solicitationNumber || '[TO BE COMPLETED]'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Offeror Name & Address</label>
                  <div className="text-bw-ink dark:text-white border-b border-bw-border dark:border-gray-600 pb-1">
                    <p className="font-medium">{content.farTable15_2CoverPage.offerorInfo?.name || '[COMPANY NAME]'}</p>
                    <p className="text-sm">{content.farTable15_2CoverPage.offerorInfo?.address || '[ADDRESS]'}</p>
                    <p className="text-sm">CAGE: {content.farTable15_2CoverPage.offerorInfo?.cageCode || 'XXXXX'} | DUNS: {content.farTable15_2CoverPage.offerorInfo?.dunsNumber || 'XX-XXX-XXXX'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Point of Contact</label>
                  <div className="text-bw-ink dark:text-white border-b border-bw-border dark:border-gray-600 pb-1 text-sm">
                    <p>{content.farTable15_2CoverPage.pointOfContact?.name || '[POC NAME]'}, {content.farTable15_2CoverPage.pointOfContact?.title || '[TITLE]'}</p>
                    <p>{content.farTable15_2CoverPage.pointOfContact?.phone || '(XXX) XXX-XXXX'} | {content.farTable15_2CoverPage.pointOfContact?.email || 'email@company.com'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Contract Type</label>
                  <p className="text-lg font-bold text-bw-primary dark:text-blue-400 border-b border-bw-border dark:border-gray-600 pb-1">
                    {content.farTable15_2CoverPage.contractType || content.contractTypeRecommendation?.recommended || 'FFP'}
                  </p>
                </div>
              </div>
              {/* Right Column */}
              <div className="space-y-4">
                <div className="p-4 bg-success-bg dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Proposed Cost Summary</label>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-bw-muted dark:text-bw-muted">Total Cost:</span>
                      <span className="font-medium text-bw-ink dark:text-white">{content.farTable15_2CoverPage.proposedCostSummary?.totalCost || '$X,XXX,XXX.XX'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-bw-muted dark:text-bw-muted">Total Profit/Fee:</span>
                      <span className="font-medium text-bw-ink dark:text-white">{content.farTable15_2CoverPage.proposedCostSummary?.totalProfit || '$XXX,XXX.XX'}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-300 dark:border-green-700">
                      <span className="font-bold text-bw-ink dark:text-white">Total Price:</span>
                      <span className="font-bold text-success dark:text-green-400 text-lg">{content.farTable15_2CoverPage.proposedCostSummary?.totalPrice || '$X,XXX,XXX.XX'}</span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-bw-muted dark:text-bw-muted space-y-2">
                  <p><span className="font-semibold">Government Property:</span> {content.farTable15_2CoverPage.governmentPropertyStatement || 'No Government property is required for contract performance.'}</p>
                  <p><span className="font-semibold">CAS Compliance:</span> {content.farTable15_2CoverPage.casComplianceStatement || 'Offeror certifies compliance with CAS as applicable.'}</p>
                  <p><span className="font-semibold">Proposal Validity:</span> {content.farTable15_2CoverPage.proposalValidityPeriod || 'Valid for 120 days from submission.'}</p>
                </div>
              </div>
            </div>
            {/* Certification & Signature Block */}
            <div className="mt-6 pt-4 border-t border-bw-border dark:border-gray-600">
              <p className="text-sm text-bw-ink dark:text-gray-300 italic mb-4">
                {content.farTable15_2CoverPage.certificationStatement || 'This proposal reflects our best estimates as of this date, in accordance with the instructions in the solicitation.'}
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Authorized Signature</label>
                  <div className="h-12 border-b-2 border-gray-400 dark:border-gray-500 flex items-end">
                    <span className="text-bw-muted dark:text-bw-muted italic text-sm">{content.farTable15_2CoverPage.authorizedSignature?.signaturePlaceholder || '[SIGNATURE REQUIRED]'}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Name & Title</label>
                  <p className="text-bw-ink dark:text-white border-b border-bw-border dark:border-gray-600 pb-1">
                    {content.farTable15_2CoverPage.authorizedSignature?.name || '[NAME]'}<br/>
                    <span className="text-sm text-bw-muted dark:text-bw-muted">{content.farTable15_2CoverPage.authorizedSignature?.title || '[TITLE]'}</span>
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Date</label>
                  <p className="text-bw-ink dark:text-white border-b border-bw-border dark:border-gray-600 pb-1">
                    {content.farTable15_2CoverPage.submissionDate || '[DATE]'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contract Type */}
      {content.contractTypeRecommendation && (
        <section className="p-4 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Contract Type</span>
              <p className="text-2xl font-bold text-bw-primary dark:text-blue-400">
                {toDisplayString(content.contractTypeRecommendation.recommended)}
              </p>
            </div>
            {content.costBreakdown?.totalContractValue && (
              <div className="text-right">
                <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Total Contract Value</span>
                <p className="text-2xl font-bold text-success dark:text-green-400">
                  {toDisplayString(content.costBreakdown.totalContractValue)}
                </p>
              </div>
            )}
          </div>
          <p className="text-sm text-bw-muted dark:text-bw-muted mt-2">{toDisplayString(content.contractTypeRecommendation.rationale)}</p>
        </section>
      )}

      {/* CLIN Structure Table - THE KEY FEDERAL TABLE */}
      {content.clinStructure && content.clinStructure.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-bw-primary" />
            Contract Line Item Number (CLIN) Structure
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">CLIN</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Description</th>
                  <th className="px-3 py-3 text-center font-semibold text-bw-ink dark:text-gray-300">Type</th>
                  <th className="px-3 py-3 text-center font-semibold text-bw-ink dark:text-gray-300">Qty</th>
                  <th className="px-3 py-3 text-center font-semibold text-bw-ink dark:text-gray-300">Unit</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Unit Price</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Total Price</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.clinStructure.map((clin: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr className={clin.clinNumber?.startsWith('1') || clin.clinNumber?.startsWith('2') ? 'bg-bw-surface-subtle dark:bg-gray-800/50' : ''}>
                      <td className="px-3 py-3 font-mono font-bold text-bw-ink dark:text-white">
                        {toDisplayString(clin.clinNumber)}
                        {clin.subClin && <span className="text-bw-muted">.{toDisplayString(clin.subClin)}</span>}
                      </td>
                      <td className="px-3 py-3 text-bw-ink dark:text-gray-300">{toDisplayString(clin.description)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          clin.contractType === 'FFP' ? 'bg-success-bg text-success dark:bg-green-900/30 dark:text-green-300' :
                          clin.contractType === 'T&M' ? 'bg-fit-moderate-bg text-fit-moderate dark:bg-yellow-900/30 dark:text-yellow-300' :
                          'bg-bw-primary-tint text-bw-primary dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {clin.contractType || 'FFP'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-bw-ink dark:text-gray-300">{toDisplayString(clin.quantity)}</td>
                      <td className="px-3 py-3 text-center text-bw-ink dark:text-gray-300">{toDisplayString(clin.unit)}</td>
                      <td className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(clin.unitPrice)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-white">{toDisplayString(clin.totalPrice)}</td>
                    </tr>
                    {/* Labor breakdown row */}
                    {clin.laborMapping && clin.laborMapping.length > 0 && (
                      <tr className="bg-bw-primary-tint/30 dark:bg-blue-900/10">
                        <td colSpan={7} className="px-6 py-2">
                          <div className="text-xs text-bw-muted dark:text-bw-muted">
                            <span className="font-medium">Labor Mapping: </span>
                            {clin.laborMapping.map((labor: any, lidx: number) => (
                              <span key={lidx} className="mr-4 inline-block">
                                {toDisplayString(labor.laborCategory)}: {typeof labor.hours === 'number' ? labor.hours.toLocaleString() : (toDisplayString(labor.hours) || 0)}h @ {toDisplayString(labor.rate)}
                                {labor.cost && <span className="font-semibold text-success dark:text-green-400"> = {toDisplayString(labor.cost)}</span>}
                              </span>
                            ))}
                          </div>
                          {joinList(clin.deliverables) && (
                            <div className="text-xs text-bw-muted dark:text-bw-muted mt-1">
                              <span className="font-medium">Deliverables: </span>
                              {joinList(clin.deliverables)}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Labor Hours Summary Table */}
      {content.laborHoursSummary && content.laborHoursSummary.rows && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Labor Hours Summary</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  {content.laborHoursSummary.headers?.map((header: string, idx: number) => (
                    <th key={idx} className={`px-3 py-3 font-semibold text-bw-ink dark:text-gray-300 ${idx === 0 ? 'text-left' : 'text-right'}`}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.laborHoursSummary.rows.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-3 py-3 text-bw-ink dark:text-white font-medium">{row.category}</td>
                    {row.hours?.map((hours: number, hidx: number) => (
                      <td key={hidx} className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">
                        {hours?.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))}
                {content.laborHoursSummary.totalHours && (
                  <tr className="bg-bw-surface-subtle dark:bg-gray-800 font-bold">
                    <td className="px-3 py-3 text-bw-ink dark:text-white">TOTAL HOURS</td>
                    {content.laborHoursSummary.totalHours.map((total: number, tidx: number) => (
                      <td key={tidx} className="px-3 py-3 text-right text-bw-ink dark:text-white">
                        {total?.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {content.laborHoursSummary.fteEquivalent && (
            <p className="text-sm text-bw-muted dark:text-bw-muted mt-2">
              <span className="font-medium">FTE Equivalent:</span> {content.laborHoursSummary.fteEquivalent}
            </p>
          )}
        </section>
      )}

      {/* Deliverable × Category Hours Matrix - CRITICAL FOR TRANSPARENCY */}
      {content.deliverableHoursTable && content.deliverableHoursTable.rows && content.deliverableHoursTable.rows.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-bw-primary" />
            Labor Hours by Deliverable × Category
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-primary-tint dark:bg-purple-900/20">
                <tr>
                  {content.deliverableHoursTable.headers?.map((header: string, idx: number) => (
                    <th key={idx} className={`px-3 py-3 font-semibold text-bw-ink dark:text-gray-300 whitespace-nowrap ${idx === 0 ? 'text-left' : 'text-right'}`}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.deliverableHoursTable.rows.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-3 py-3 text-bw-ink dark:text-white font-medium">{toDisplayString(row.deliverable)}</td>
                    {row.hours?.map((hours: number, hidx: number) => (
                      <td key={hidx} className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">
                        {typeof hours === 'number' && hours > 0 ? hours.toLocaleString() : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-3 text-right font-medium text-bw-ink dark:text-white">{typeof row.totalHours === 'number' ? row.totalHours.toLocaleString() : toDisplayString(row.totalHours)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-success dark:text-green-400">{toDisplayString(row.totalCost)}</td>
                  </tr>
                ))}
                {content.deliverableHoursTable.totals && (
                  <tr className="bg-bw-primary-tint dark:bg-purple-900/30 font-bold">
                    <td className="px-3 py-3 text-bw-ink dark:text-white">{toDisplayString(content.deliverableHoursTable.totals.label)}</td>
                    {content.deliverableHoursTable.totals.hours?.map((total: number, tidx: number) => (
                      <td key={tidx} className="px-3 py-3 text-right text-bw-ink dark:text-white">
                        {typeof total === 'number' && total > 0 ? total.toLocaleString() : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-3 text-right text-bw-ink dark:text-white">{typeof content.deliverableHoursTable.totals.totalHours === 'number' ? content.deliverableHoursTable.totals.totalHours.toLocaleString() : toDisplayString(content.deliverableHoursTable.totals.totalHours)}</td>
                    <td className="px-3 py-3 text-right text-success dark:text-green-300">{toDisplayString(content.deliverableHoursTable.totals.totalCost)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Labor Categories with SCA/GSA */}
      {content.laborCategories && content.laborCategories.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Labor Categories & Rate Structure</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Category</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">SCA Equivalent</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">GSA Equivalent</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Experience</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Education</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Hourly Rate</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Loaded Rate</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.laborCategories.map((cat: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr>
                      <td className="px-3 py-3 text-bw-ink dark:text-white font-medium">{toDisplayString(cat.category)}</td>
                      <td className="px-3 py-3 text-bw-muted dark:text-bw-muted text-xs">{toDisplayString(cat.scaEquivalent || cat.equivalent) || '-'}</td>
                      <td className="px-3 py-3 text-bw-muted dark:text-bw-muted text-xs">{toDisplayString(cat.gsaEquivalent) || '-'}</td>
                      <td className="px-3 py-3 text-bw-ink dark:text-gray-300">{toDisplayString(cat.minExperience)}</td>
                      <td className="px-3 py-3 text-bw-ink dark:text-gray-300">{toDisplayString(cat.education)}</td>
                      <td className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(cat.hourlyRate)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-white">{toDisplayString(cat.loadedRate)}</td>
                    </tr>
                    {(cat.description || cat.tasksPerformed) && (
                      <tr className="bg-bw-surface-subtle/50 dark:bg-gray-800/30">
                        <td colSpan={7} className="px-6 py-2 text-xs text-bw-muted dark:text-bw-muted">
                          {cat.description && <p>{toDisplayString(cat.description)}</p>}
                          {joinList(cat.tasksPerformed) && (
                            <p className="mt-1"><span className="font-medium">Tasks:</span> {joinList(cat.tasksPerformed)}</p>
                          )}
                          {cat.clearanceRequired && (
                            <p className="mt-1"><span className="font-medium">Clearance:</span> {toDisplayString(cat.clearanceRequired)}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Labor Rate Buildup Table - MANDATORY: Shows Base → Fringe → Overhead → G&A → Profit calculation */}
      {content.laborRateBuildup && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-bw-primary" />
            Labor Rate Buildup (Base → Loaded Rate Calculation)
          </h2>
          {content.laborRateBuildup.narrative && (
            <p className="text-sm text-bw-muted dark:text-bw-muted mb-4">{content.laborRateBuildup.narrative}</p>
          )}
          {content.laborRateBuildup.buildupTable && content.laborRateBuildup.buildupTable.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
                <thead className="bg-bw-primary-tint dark:bg-purple-900/20">
                  <tr>
                    <th className="px-2 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Labor Category</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Base Rate</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">+ Fringe</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Subtotal</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">+ Overhead</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Subtotal</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">+ G&A</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Total Cost</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">+ Profit</th>
                    <th className="px-2 py-3 text-right font-semibold text-bw-primary dark:text-purple-400 bg-bw-primary-tint dark:bg-purple-900/40">Loaded Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                  {content.laborRateBuildup.buildupTable.map((row: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-2 py-3 text-bw-ink dark:text-white font-medium">{toDisplayString(row.laborCategory)}</td>
                      <td className="px-2 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(row.baseRate)}</td>
                      <td className="px-2 py-3 text-right text-bw-muted dark:text-bw-muted text-xs">
                        {toDisplayString(row.fringeRate)}<br/>
                        <span className="text-bw-muted">({toDisplayString(row.fringePercent)})</span>
                      </td>
                      <td className="px-2 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(row.subtotalWithFringe)}</td>
                      <td className="px-2 py-3 text-right text-bw-muted dark:text-bw-muted text-xs">
                        {toDisplayString(row.overheadRate)}<br/>
                        <span className="text-bw-muted">({toDisplayString(row.overheadPercent)})</span>
                      </td>
                      <td className="px-2 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(row.subtotalWithOverhead)}</td>
                      <td className="px-2 py-3 text-right text-bw-muted dark:text-bw-muted text-xs">
                        {toDisplayString(row.gaRate)}<br/>
                        <span className="text-bw-muted">({toDisplayString(row.gaPercent)})</span>
                      </td>
                      <td className="px-2 py-3 text-right text-bw-ink dark:text-gray-300">{toDisplayString(row.totalCost)}</td>
                      <td className="px-2 py-3 text-right text-bw-muted dark:text-bw-muted text-xs">
                        {toDisplayString(row.profitRate)}<br/>
                        <span className="text-bw-muted">({toDisplayString(row.profitPercent)})</span>
                      </td>
                      <td className="px-2 py-3 text-right font-bold text-bw-primary dark:text-purple-400 bg-bw-primary-tint dark:bg-purple-900/20">{toDisplayString(row.loadedRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {content.laborRateBuildup.rateSourceJustification && (
            <p className="text-sm text-bw-muted dark:text-bw-muted mt-4 italic">
              <span className="font-medium">Rate Source:</span> {toDisplayString(content.laborRateBuildup.rateSourceJustification)}
            </p>
          )}
        </section>
      )}

      {/* Indirect Rates with Components */}
      {content.indirectRates && content.indirectRates.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Indirect Rate Structure</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {content.indirectRates.map((rate: any, idx: number) => (
              <div key={idx} className="p-4 bg-bw-surface dark:bg-gray-800 rounded-lg border border-bw-border dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-bw-ink dark:text-white">{toDisplayString(rate.category)}</span>
                  <span className="text-xl font-bold text-bw-primary dark:text-blue-400">{toDisplayString(rate.rate)}</span>
                </div>
                <div className="text-sm text-bw-muted dark:text-bw-muted">
                  <p><span className="font-medium">Basis:</span> {toDisplayString(rate.basis)}</p>
                  <p><span className="font-medium">Audit Status:</span> {toDisplayString(rate.auditStatus)}</p>
                  {joinList(rate.components) && (
                    <p className="mt-1"><span className="font-medium">Components:</span> {joinList(rate.components)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Cost Breakdown */}
      {content.costBreakdown && content.costBreakdown.headers && content.costBreakdown.rows && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Cost Summary by Period</h2>
          {content.costBreakdown.assumptions && (
            <p className="text-sm text-bw-muted dark:text-bw-muted mb-4 italic">{toDisplayString(content.costBreakdown.assumptions)}</p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  {content.costBreakdown.headers.map((header: string, idx: number) => (
                    <th key={idx} className={`px-3 py-3 font-semibold text-bw-ink dark:text-gray-300 ${idx === 0 ? 'text-left' : 'text-right'}`}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.costBreakdown.rows.map((row: any, idx: number) => (
                  <tr key={idx} className={row.element?.toLowerCase().includes('total') ? 'bg-bw-surface-subtle dark:bg-gray-800 font-bold' : ''}>
                    <td className="px-3 py-3 text-bw-ink dark:text-white">{toDisplayString(row.element)}</td>
                    {row.values?.map((val: any, vidx: number) => (
                      <td key={vidx} className="px-3 py-3 text-right text-bw-ink dark:text-white">{toDisplayString(val)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {content.costBreakdown.costNarrative && (
            <p className="text-sm text-bw-muted dark:text-bw-muted mt-4">{toDisplayString(content.costBreakdown.costNarrative)}</p>
          )}
        </section>
      )}

      {/* SCA Wage Determination */}
      {content.scaWageDetermination && content.scaWageDetermination.applicable && (
        <section className="p-4 bg-fit-moderate-bg dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <h2 className="text-lg font-bold text-bw-ink dark:text-white mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-fit-moderate" />
            Service Contract Act (SCA) Wage Determination
          </h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-bw-ink dark:text-gray-300">Wage Decision:</span>
              <p className="text-bw-ink dark:text-white">{toDisplayString(content.scaWageDetermination.wageDecisionNumber)}</p>
            </div>
            <div>
              <span className="font-medium text-bw-ink dark:text-gray-300">Locality:</span>
              <p className="text-bw-ink dark:text-white">{toDisplayString(content.scaWageDetermination.locality)}</p>
            </div>
            <div>
              <span className="font-medium text-bw-ink dark:text-gray-300">Applicable Categories:</span>
              <p className="text-bw-ink dark:text-white">{joinList(content.scaWageDetermination.applicableCategories)}</p>
            </div>
          </div>
          {content.scaWageDetermination.minimumWages && content.scaWageDetermination.minimumWages.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-yellow-300 dark:border-yellow-700">
                    <th className="px-3 py-2 text-left font-medium text-bw-ink dark:text-gray-300">Category</th>
                    <th className="px-3 py-2 text-right font-medium text-bw-ink dark:text-gray-300">SCA Minimum</th>
                    <th className="px-3 py-2 text-right font-medium text-bw-ink dark:text-gray-300">Proposed Rate</th>
                    <th className="px-3 py-2 text-right font-medium text-bw-ink dark:text-gray-300">H&W</th>
                  </tr>
                </thead>
                <tbody>
                  {content.scaWageDetermination.minimumWages.map((wage: any, idx: number) => (
                    <tr key={idx} className="border-b border-yellow-200 dark:border-yellow-800/50">
                      <td className="px-3 py-2 text-bw-ink dark:text-white">{wage.category}</td>
                      <td className="px-3 py-2 text-right text-bw-ink dark:text-gray-300">{wage.minimumRate}</td>
                      <td className="px-3 py-2 text-right font-medium text-success dark:text-green-400">{wage.proposedRate}</td>
                      <td className="px-3 py-2 text-right text-bw-ink dark:text-gray-300">{wage.healthAndWelfare}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Basis of Estimate */}
      {content.basisOfEstimate && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Basis of Estimate (BOE) - FAR Table 15-2 Section I.C</h2>
          {content.basisOfEstimate.estimateConfidenceLevel && (
            <div className="mb-4 p-3 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg inline-block">
              <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Estimate Confidence: </span>
              <span className={`font-bold ${
                content.basisOfEstimate.estimateConfidenceLevel.toLowerCase().includes('high') ? 'text-success dark:text-green-400' :
                content.basisOfEstimate.estimateConfidenceLevel.toLowerCase().includes('medium') ? 'text-fit-moderate dark:text-yellow-400' :
                'text-urgent dark:text-red-400'
              }`}>
                {content.basisOfEstimate.estimateConfidenceLevel}
              </span>
            </div>
          )}
          <div className="space-y-4">
            {content.basisOfEstimate.methodology && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Methodology</h3>
                <p className="text-bw-ink dark:text-gray-300">{renderText(content.basisOfEstimate.methodology)}</p>
              </div>
            )}
            {content.basisOfEstimate.laborHoursJustification && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Labor Hours Justification</h3>
                <p className="text-bw-ink dark:text-gray-300">{renderText(content.basisOfEstimate.laborHoursJustification)}</p>
              </div>
            )}
            {content.basisOfEstimate.historicalDataSources && content.basisOfEstimate.historicalDataSources.length > 0 && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Historical Data Sources</h3>
                <ul className="list-disc list-inside text-bw-ink dark:text-gray-300">
                  {content.basisOfEstimate.historicalDataSources.map((source: string, idx: number) => (
                    <li key={idx}>{source}</li>
                  ))}
                </ul>
              </div>
            )}
            {content.basisOfEstimate.assumptions && content.basisOfEstimate.assumptions.length > 0 && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Key Assumptions</h3>
                <ul className="list-disc list-inside text-bw-ink dark:text-gray-300">
                  {content.basisOfEstimate.assumptions.map((assumption: string, idx: number) => (
                    <li key={idx}>{assumption}</li>
                  ))}
                </ul>
              </div>
            )}
            {content.basisOfEstimate.exclusions && content.basisOfEstimate.exclusions.length > 0 && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Exclusions (Not Included in Estimate)</h3>
                <ul className="list-disc list-inside text-bw-ink dark:text-gray-300">
                  {content.basisOfEstimate.exclusions.map((exclusion: string, idx: number) => (
                    <li key={idx}>{exclusion}</li>
                  ))}
                </ul>
              </div>
            )}
            {content.basisOfEstimate.risks && content.basisOfEstimate.risks.length > 0 && (
              <div>
                <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Risk Factors & Mitigations</h3>
                <ul className="list-disc list-inside text-bw-ink dark:text-gray-300">
                  {content.basisOfEstimate.risks.map((risk: string, idx: number) => (
                    <li key={idx}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Price Realism */}
      {content.priceRealism && (
        <section className="p-4 bg-success-bg dark:bg-green-900/20 rounded-lg">
          <h2 className="text-lg font-bold text-bw-ink dark:text-white mb-3">Price Realism Analysis</h2>
          {content.priceRealism.narrative && (
            <p className="text-bw-ink dark:text-gray-300 mb-4">{renderText(content.priceRealism.narrative)}</p>
          )}
          {content.priceRealism.benchmarks && content.priceRealism.benchmarks.length > 0 && (
            <div className="grid md:grid-cols-3 gap-3">
              {content.priceRealism.benchmarks.map((benchmark: any, idx: number) => (
                <div key={idx} className="p-3 bg-bw-surface dark:bg-gray-800 rounded border border-green-200 dark:border-green-800">
                  <p className="font-medium text-bw-ink dark:text-white text-sm">{benchmark.source}</p>
                  <p className="text-bw-muted dark:text-bw-muted text-xs">{benchmark.comparison}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Other Direct Costs */}
      {content.otherDirectCosts && content.otherDirectCosts.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Other Direct Costs (ODCs)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700 text-sm">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Category</th>
                  <th className="px-3 py-3 text-left font-semibold text-bw-ink dark:text-gray-300">Description</th>
                  <th className="px-3 py-3 text-center font-semibold text-bw-ink dark:text-gray-300">Quantity</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Unit Cost</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Annual</th>
                  <th className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-gray-300">Total</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.otherDirectCosts.map((odc: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr>
                      <td className="px-3 py-3 text-bw-ink dark:text-white font-medium">{odc.category}</td>
                      <td className="px-3 py-3 text-bw-ink dark:text-gray-300">{odc.description}</td>
                      <td className="px-3 py-3 text-center text-bw-ink dark:text-gray-300">{odc.quantity}</td>
                      <td className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">{odc.unitCost}</td>
                      <td className="px-3 py-3 text-right text-bw-ink dark:text-gray-300">{odc.annualTotal}</td>
                      <td className="px-3 py-3 text-right font-semibold text-bw-ink dark:text-white">{odc.totalAllYears || odc.total}</td>
                    </tr>
                    {odc.justification && (
                      <tr className="bg-bw-surface-subtle/50 dark:bg-gray-800/30">
                        <td colSpan={6} className="px-6 py-2 text-xs text-bw-muted dark:text-bw-muted italic">
                          Justification: {odc.justification}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Value Proposition */}
      {content.valueProposition && (
        <section className="p-6 bg-bw-surface-subtle dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Best Value Determination</h2>
          {content.valueProposition.efficiencyMeasures && content.valueProposition.efficiencyMeasures.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Efficiency Measures</h3>
              <ul className="space-y-2">
                {content.valueProposition.efficiencyMeasures.map((measure: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-bw-ink dark:text-gray-300">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>{measure}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {content.valueProposition.costSavingsApproach && (
            <div className="mb-4">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Cost Savings Approach</h3>
              <p className="text-bw-ink dark:text-gray-300">{content.valueProposition.costSavingsApproach}</p>
            </div>
          )}
          {content.valueProposition.bestValueDetermination && (
            <div>
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Best Value Summary</h3>
              <p className="text-bw-ink dark:text-gray-300">{content.valueProposition.bestValueDetermination}</p>
            </div>
          )}
        </section>
      )}

      {/* Cost Certification Statement - MANDATORY PER FAR 15.406-2 */}
      {content.costCertification && (
        <section className="border-2 border-red-200 dark:border-red-800 rounded-lg overflow-hidden">
          <div className="bg-urgent-bg dark:bg-red-900/30 p-3 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-urgent dark:text-red-400" />
            <h2 className="text-lg font-bold text-urgent dark:text-red-300">Cost Certification Statement (FAR 15.406-2)</h2>
          </div>
          <div className="p-6 bg-bw-surface dark:bg-gray-800 space-y-4">
            <div className="p-4 bg-bw-surface-subtle dark:bg-gray-900 rounded border border-bw-border dark:border-gray-700">
              <p className="text-sm text-bw-ink dark:text-gray-300 font-mono whitespace-pre-wrap">
                {content.costCertification.certificationText || 'CERTIFICATE OF CURRENT COST OR PRICING DATA: This is to certify that, to the best of my knowledge and belief, the cost or pricing data submitted are accurate, complete, and current as of the date of agreement on price.'}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Applicability</label>
                <p className="text-bw-ink dark:text-gray-300">{content.costCertification.applicability || 'Required for contracts exceeding $2,000,000 threshold per FAR 15.403-4.'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Exceptions Claimed</label>
                <p className="text-bw-ink dark:text-gray-300">{content.costCertification.exceptionsClaimed || 'None'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Data Submitted</label>
                <p className="text-bw-ink dark:text-gray-300">{content.costCertification.dataSubmitted || 'Labor rates, indirect rates, material costs, subcontractor quotes.'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-bw-muted dark:text-bw-muted uppercase">Data Currency Date</label>
                <p className="text-bw-ink dark:text-gray-300">{content.costCertification.currencyDate || '[TO BE COMPLETED AT SUBMISSION]'}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-bw-border dark:border-gray-700">
              <p className="text-xs text-bw-muted dark:text-bw-muted italic">
                Note: This certification will be executed by an authorized company representative at the time of proposal submission.
                The Truth in Negotiations Act (TINA) requires that cost or pricing data be accurate, complete, and current.
                Defective pricing may result in price reduction and potential penalties.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// USER-FRIENDLY PRICING VIEW (Original simplified view)
// ============================================================================

function UserFriendlyPricingView({
  content,
  showCitations,
  renderText
}: {
  content: any;
  showCitations: boolean;
  renderText: (text: string) => React.ReactNode;
}) {
  return (
    <div className="space-y-8">
      {/* Contract Type Recommendation */}
      {content.contractTypeRecommendation && (
        <section className="p-6 bg-bw-primary-tint dark:bg-blue-900/20 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-bw-primary" />
            Contract Type Recommendation
          </h2>
          <p className="text-2xl font-bold text-bw-primary dark:text-blue-400 mb-2">
            {content.contractTypeRecommendation.recommended}
          </p>
          <p className="text-bw-ink dark:text-gray-300">{content.contractTypeRecommendation.rationale}</p>
        </section>
      )}

      {/* CLIN Structure - Simplified View */}
      {content.clinStructure && content.clinStructure.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-bw-primary" />
            Contract Line Items (CLINs)
          </h2>
          <div className="space-y-3">
            {content.clinStructure.filter((clin: any) => clin.clinNumber !== '9999').map((clin: any, idx: number) => (
              <div key={idx} className="p-4 bg-bw-surface dark:bg-gray-800 rounded-lg border border-bw-border dark:border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-mono text-sm text-bw-primary dark:text-blue-400 mr-2">CLIN {clin.clinNumber}</span>
                    <span className="font-semibold text-bw-ink dark:text-white">{clin.description}</span>
                  </div>
                  <span className="text-lg font-bold text-success dark:text-green-400">{clin.totalPrice}</span>
                </div>
                <div className="text-sm text-bw-muted dark:text-bw-muted flex items-center gap-4">
                  <span>{clin.quantity?.toLocaleString()} {clin.unit}</span>
                  <span className="text-bw-muted">|</span>
                  <span>Unit Price: {clin.unitPrice}</span>
                </div>
                {clin.laborMapping && clin.laborMapping.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-bw-border dark:border-gray-700">
                    <p className="text-xs font-medium text-bw-muted dark:text-bw-muted mb-2">Labor Breakdown:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {clin.laborMapping.map((labor: any, lidx: number) => (
                        <div key={lidx} className="text-xs bg-bw-surface-subtle dark:bg-gray-700/50 rounded px-2 py-1">
                          <span className="font-medium text-bw-ink dark:text-gray-300">{labor.laborCategory}</span>
                          <span className="text-bw-muted dark:text-bw-muted ml-1">{labor.hours}h</span>
                          {labor.cost && <span className="text-success dark:text-green-400 ml-1">{labor.cost}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {/* Total row */}
            {content.clinStructure.find((c: any) => c.clinNumber === '9999') && (
              <div className="p-4 bg-success-bg dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-bw-ink dark:text-white">TOTAL CONTRACT VALUE</span>
                  <span className="text-2xl font-bold text-success dark:text-green-400">
                    {content.clinStructure.find((c: any) => c.clinNumber === '9999')?.totalPrice}
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Deliverable Hours Table - Simplified */}
      {content.deliverableHoursTable && content.deliverableHoursTable.rows && content.deliverableHoursTable.rows.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Hours by Deliverable</h2>
          <div className="space-y-2">
            {content.deliverableHoursTable.rows.map((row: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-bw-surface-subtle dark:bg-gray-800 rounded-lg">
                <span className="font-medium text-bw-ink dark:text-white">{row.deliverable}</span>
                <div className="text-right">
                  <span className="text-bw-muted dark:text-bw-muted mr-4">{row.totalHours?.toLocaleString()} hours</span>
                  <span className="font-semibold text-success dark:text-green-400">{row.totalCost}</span>
                </div>
              </div>
            ))}
            {content.deliverableHoursTable.totals && (
              <div className="flex items-center justify-between p-3 bg-bw-primary-tint dark:bg-purple-900/30 rounded-lg font-bold">
                <span className="text-bw-ink dark:text-white">TOTAL</span>
                <div className="text-right">
                  <span className="text-bw-ink dark:text-gray-300 mr-4">{content.deliverableHoursTable.totals.totalHours?.toLocaleString()} hours</span>
                  <span className="text-success dark:text-green-300">{content.deliverableHoursTable.totals.totalCost}</span>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Labor Categories Table */}
      {content.laborCategories && content.laborCategories.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Labor Categories</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Category</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Equivalent</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Experience</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Education</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Hourly</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Loaded</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.laborCategories.map((cat: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-white font-medium">{cat.category}</td>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-gray-300">{cat.equivalent}</td>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-gray-300">{cat.minExperience}</td>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-gray-300">{cat.education}</td>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-gray-300 text-right">{cat.hourlyRate}</td>
                      <td className="px-3 py-3 text-sm text-bw-ink dark:text-white font-medium text-right">{cat.loadedRate}</td>
                    </tr>
                    {/* Show description and tasks if available */}
                    {(cat.description || (cat.tasksPerformed && cat.tasksPerformed.length > 0)) && (
                      <tr className="bg-bw-surface-subtle dark:bg-gray-800/50">
                        <td colSpan={6} className="px-3 py-2 text-sm">
                          {cat.description && (
                            <p className="text-bw-muted dark:text-bw-muted mb-1">{cat.description}</p>
                          )}
                          {joinList(cat.tasksPerformed) && (
                            <div className="text-bw-muted dark:text-bw-muted">
                              <span className="font-medium">Tasks: </span>
                              {joinList(cat.tasksPerformed)}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Rate Methodology */}
      {content.rateMethodology && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Rate Development Methodology</h2>
          <p className="text-bw-ink dark:text-gray-300 leading-relaxed">{renderText(content.rateMethodology)}</p>
        </section>
      )}

      {/* Indirect Rates */}
      {content.indirectRates && content.indirectRates.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Indirect Rate Structure</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Basis</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Audit Status</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.indirectRates.map((rate, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white">{rate.category}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-medium text-right">{rate.rate}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{rate.basis}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{rate.auditStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Cost Breakdown */}
      {content.costBreakdown && content.costBreakdown.headers && content.costBreakdown.rows && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Cost Breakdown Structure</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  {content.costBreakdown.headers.map((header, idx) => (
                    <th
                      key={idx}
                      className={`px-4 py-3 text-xs font-medium text-bw-muted dark:text-bw-muted uppercase ${idx === 0 ? 'text-left' : 'text-right'}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.costBreakdown.rows.map((row: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr className={row.element?.toLowerCase().includes('total') ? 'bg-bw-surface-subtle dark:bg-gray-800 font-bold' : ''}>
                      <td className="px-4 py-3 text-sm text-bw-ink dark:text-white">{row.element}</td>
                      {row.values?.map((val: string, vidx: number) => (
                        <td key={vidx} className="px-4 py-3 text-sm text-bw-ink dark:text-white text-right">{val}</td>
                      ))}
                    </tr>
                    {/* Show description and justification if available */}
                    {(row.description || row.justification) && (
                      <tr className="bg-bw-primary-tint/50 dark:bg-blue-900/10">
                        <td colSpan={content.costBreakdown.headers.length} className="px-4 py-2 text-sm">
                          {row.description && (
                            <p className="text-bw-muted dark:text-bw-muted">{row.description}</p>
                          )}
                          {row.justification && (
                            <p className="text-bw-muted dark:text-bw-muted italic mt-1">
                              <span className="font-medium">Justification:</span> {row.justification}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {/* Cost Narrative */}
          {content.costBreakdown?.costNarrative && (
            <div className="mt-4 p-4 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Cost Narrative</h3>
              <p className="text-bw-ink dark:text-gray-300">{content.costBreakdown.costNarrative}</p>
            </div>
          )}
          {/* Total Projected Cost */}
          {content.costBreakdown?.totalProjectedCost && (
            <div className="mt-4 p-4 bg-success-bg dark:bg-green-900/20 rounded-lg text-center">
              <p className="text-sm text-bw-muted dark:text-bw-muted">Total Projected Cost</p>
              <p className="text-3xl font-bold text-success dark:text-green-400">{content.costBreakdown.totalProjectedCost}</p>
            </div>
          )}
        </section>
      )}

      {/* Line Item Details - NEW SECTION */}
      {content.lineItemDetails && content.lineItemDetails.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-bw-primary" />
            Detailed Line Items
          </h2>
          <div className="space-y-4">
            {content.lineItemDetails.map((item: any, idx: number) => {
              // The deterministic pricing engine emits line items as
              // { item, description, quantity, unit, unitRate, loadedRate, totalCost, basis, justification },
              // while the LLM-regenerate path emits { item, amount, purpose, deliverables, timeframe }.
              // Read both shapes so the row renders either way.
              const amount = item.amount || item.totalCost;
              const purpose = item.purpose || item.description || item.justification;
              const hasRateRow = item.quantity != null || item.unitRate || item.loadedRate || item.basis;
              return (
                <div
                  key={idx}
                  className="p-4 bg-bw-surface dark:bg-gray-800 rounded-lg border border-bw-border dark:border-gray-700"
                >
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <h3 className="font-semibold text-bw-ink dark:text-white">{item.item}</h3>
                    {amount && (
                      <span className="px-3 py-1 bg-bw-primary-tint dark:bg-blue-900/30 text-bw-primary dark:text-blue-300 rounded-full text-sm font-medium whitespace-nowrap">
                        {amount}
                      </span>
                    )}
                  </div>
                  {purpose && <p className="text-bw-ink dark:text-gray-300 mb-2">{purpose}</p>}
                  {hasRateRow && (
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-2">
                      {item.quantity != null && (
                        <span className="text-bw-muted dark:text-bw-muted">
                          Hours: <span className="text-bw-ink dark:text-gray-300 font-medium">{Number(item.quantity).toLocaleString()}{item.unit ? ` ${item.unit}` : ''}</span>
                        </span>
                      )}
                      {item.unitRate && (
                        <span className="text-bw-muted dark:text-bw-muted">
                          Base: <span className="text-bw-ink dark:text-gray-300 font-medium">{item.unitRate}</span>
                        </span>
                      )}
                      {item.loadedRate && (
                        <span className="text-bw-muted dark:text-bw-muted">
                          Loaded: <span className="text-bw-ink dark:text-gray-300 font-medium">{item.loadedRate}</span>
                        </span>
                      )}
                      {item.basis && (
                        <span className="text-bw-muted dark:text-bw-muted">
                          Basis: <span className="text-bw-ink dark:text-gray-300 font-medium">{item.basis}</span>
                        </span>
                      )}
                    </div>
                  )}
                  {joinList(item.deliverables) && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-bw-muted dark:text-bw-muted">Deliverables: </span>
                      <span className="text-sm text-bw-ink dark:text-gray-300">{joinList(item.deliverables)}</span>
                    </div>
                  )}
                  {item.timeframe && (
                    <div className="text-sm text-bw-muted dark:text-bw-muted">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {item.timeframe}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Funding Allocation - NEW SECTION */}
      {content.fundingAllocation && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-bw-primary" />
            Funding Allocation
          </h2>
          {content.fundingAllocation.summary && (
            <p className="text-bw-ink dark:text-gray-300 mb-4">{content.fundingAllocation.summary}</p>
          )}
          {content.fundingAllocation.categories && content.fundingAllocation.categories.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {content.fundingAllocation.categories.map((cat: any, idx: number) => (
                <div
                  key={idx}
                  className="p-4 bg-bw-surface-subtle dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border-l-4 border-purple-500"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-bw-ink dark:text-white">{cat.category}</h3>
                    <div className="text-right">
                      <span className="text-lg font-bold text-bw-primary dark:text-purple-400">{cat.percentage}</span>
                      {cat.amount && <span className="text-sm text-bw-muted dark:text-bw-muted ml-2">({cat.amount})</span>}
                    </div>
                  </div>
                  {cat.description && (
                    <p className="text-sm text-bw-muted dark:text-bw-muted">{cat.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Value Proposition */}
      {content.valueProposition && (
        <section className="p-6 bg-success-bg dark:bg-green-900/20 rounded-lg">
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Value Proposition</h2>
          {content.valueProposition.efficiencyMeasures && content.valueProposition.efficiencyMeasures.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Efficiency Measures</h3>
              <ul className="space-y-2">
                {content.valueProposition.efficiencyMeasures.map((measure, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-bw-ink dark:text-gray-300">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>{measure}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {content.valueProposition.bestValueDetermination && (
            <div className="mb-4">
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Best Value Determination</h3>
              <p className="text-bw-ink dark:text-gray-300">{content.valueProposition.bestValueDetermination}</p>
            </div>
          )}
          {/* Cost Savings Approach - NEW */}
          {(content.valueProposition as any)?.costSavingsApproach && (
            <div>
              <h3 className="font-semibold text-bw-ink dark:text-white mb-2">Cost Savings Approach</h3>
              <p className="text-bw-ink dark:text-gray-300">{(content.valueProposition as any).costSavingsApproach}</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ============================================================================
// COMPLIANCE RENDERER
// ============================================================================

interface ComplianceRendererProps {
  content: ComplianceContent;
  showCitations?: boolean;
}

export function ComplianceRenderer({ content, showCitations = true }: ComplianceRendererProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-success" />;
      case 'partial':
        return <AlertTriangle className="w-5 h-5 text-fit-moderate" />;
      case 'gap':
        return <XCircle className="w-5 h-5 text-urgent" />;
      default:
        return <CheckCircle className="w-5 h-5 text-bw-muted" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      compliant: 'bg-success-bg text-success dark:bg-green-900/30 dark:text-green-300',
      partial: 'bg-fit-moderate-bg text-fit-moderate dark:bg-yellow-900/30 dark:text-yellow-300',
      gap: 'bg-urgent-bg text-urgent dark:bg-red-900/30 dark:text-red-300',
    };
    return styles[status as keyof typeof styles] || styles.compliant;
  };

  return (
    <div className="space-y-8">
      {/* FAR Compliance */}
      {content.farCompliance && content.farCompliance.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-bw-primary" />
            FAR Compliance Matrix
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Clause</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Title</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.farCompliance.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-mono">{item.clause}</td>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{item.title}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{item.notes}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* DFARS Compliance (if present) */}
      {content.dfarsCompliance && content.dfarsCompliance.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">DFARS Compliance Matrix</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bw-border dark:divide-gray-700">
              <thead className="bg-bw-surface-subtle dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Clause</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Title</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-bw-muted dark:text-bw-muted uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-bw-surface dark:bg-gray-900 divide-y divide-bw-border dark:divide-gray-700">
                {content.dfarsCompliance.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-white font-mono">{item.clause}</td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{item.title}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                        {getStatusIcon(item.status)}
                        <span className="ml-1 capitalize">{item.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-bw-ink dark:text-gray-300">{item.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Certifications */}
      {content.certifications && content.certifications.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Certifications & Registrations</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {content.certifications.map((cert, idx) => (
              <div
                key={idx}
                className="p-4 bg-bw-surface dark:bg-gray-800 rounded-lg border border-bw-border dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-bw-ink dark:text-white">{cert.name}</span>
                  <span className="px-2 py-1 bg-success-bg dark:bg-green-900/30 text-success dark:text-green-300 rounded text-xs">
                    {cert.status}
                  </span>
                </div>
                {cert.number && (
                  <p className="text-sm text-bw-muted dark:text-bw-muted">{cert.number}</p>
                )}
                {cert.expiration && (
                  <p className="text-sm text-bw-muted dark:text-bw-muted">Expires: {cert.expiration}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Security Clearances */}
      {content.securityClearances && content.securityClearances.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Security Clearances</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {content.securityClearances.map((clearance, idx) => (
              <div
                key={idx}
                className="p-4 bg-bw-primary-tint dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-bw-primary" />
                  <span className="font-semibold text-bw-ink dark:text-white">{clearance.type}</span>
                </div>
                <p className="text-lg font-bold text-bw-primary dark:text-purple-400">{clearance.level}</p>
                <p className="text-sm text-bw-muted dark:text-bw-muted">{clearance.status}</p>
                {clearance.agency && (
                  <p className="text-sm text-bw-muted dark:text-bw-muted">Agency: {clearance.agency}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Acknowledgments */}
      <section className="p-6 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg">
        <h2 className="text-xl font-bold text-bw-ink dark:text-white mb-4">Compliance Acknowledgments</h2>
        <p className="text-bw-ink dark:text-gray-300 leading-relaxed">{content.acknowledgments}</p>
      </section>
    </div>
  );
}

// ============================================================================
// EXPORT ALL RENDERERS
// ============================================================================

export {
  highlightCitations,
};
